/**
 * V2 Sidebar Navigation Constants
 *
 * Hierarchical navigation structure for the FundsChain 2.0 sidebar.
 * All labels use translation keys from public/locales/en/translation.json
 */

// Icons for menu items (to be imported in component)
import {
	AccountTree as IconActivity,
	AccountBalance as IconFinancing,
	AccountBalanceWallet as IconAccount,
	Assessment as IconReport,
	Assignment as IconWorkPlan,
	AutoAwesome as IconAIChat,
	Business as IconEntities,
	Category as IconComponentsCategories,
	ConfirmationNumber as IconPayments,
	Dashboard as IconDashboard,
	Description as IconContract,
	FolderOpen as IconDocuments,
	Group as IconVerificationGroup,
	GppGood as IconCondition,
	Payments as IconSpendPlan,
	People as IconBeneficiary,
	Receipt as IconInvoice,
	Savings as IconEarmark,
	Settings as IconSettings,
	Store as IconSupplier,
	TableChart as IconCustomTemplates,
} from '@mui/icons-material';
import { SvgIconComponent } from '@mui/icons-material';

// Types

export interface NavMenuItem {
	/** Unique identifier for the menu item */
	id: string;
	/** Translation key for the menu label */
	labelKey: string;
	/** Route path segment (relative to /project/:projectId/spend-plans, or to
	 * /project/:projectId when `absolute` is true) */
	path: string;
	/** MUI icon component */
	icon: SvgIconComponent;
	/** Value used for tab/route matching */
	value: string;
	/** Optional: mark item as coming soon or disabled */
	disabled?: boolean;
	/** Optional: badge count or indicator */
	badge?: 'new' | 'beta' | number;
	/** When true, `path` is resolved under /project/:projectId/ (i.e. a v1 route
	 * outside the spend-plans subtree). Default: false. */
	absolute?: boolean;
}

export interface NavMenuGroup {
	/** Unique identifier for the group */
	id: string;
	/** Translation key for the group label */
	labelKey: string;
	/** Menu items in this group */
	items: NavMenuItem[];
	/** Optional: show divider after group */
	showDivider?: boolean;
}

export interface NavMenuConfig {
	/** All navigation groups */
	groups: NavMenuGroup[];
	/** Flat list of all items (for easy lookup) */
	allItems: NavMenuItem[];
}

// Navigation Items

/**
 * Planning Summary (formerly "Finance Overview")
 * Spend-plans index dashboard. Now surfaced inside the Activity Planning group
 * because the project Overview tab again renders the v1 project overview.
 */
export const NAV_ITEM_FINANCE_OVERVIEW: NavMenuItem = {
	id: 'finance-overview',
	labelKey: 'spendPlan.planningSummary',
	path: '',
	icon: IconDashboard,
	value: 'dashboard',
};

/**
 * Activity Planning Group
 * Activities and work planning
 */
export const NAV_GROUP_ACTIVITY_PLANNING: NavMenuGroup = {
	id: 'activity-planning',
	labelKey: 'spendPlan.nav.group.activityPlanning',
	showDivider: true,
	items: [
		NAV_ITEM_FINANCE_OVERVIEW,
		{
			id: 'components-categories',
			labelKey: 'spendPlan.componentsAndCategories',
			path: 'components-categories',
			icon: IconComponentsCategories,
			value: 'components-categories',
		},
		{
			id: 'work-planning',
			labelKey: 'spendPlan.workPlanning',
			path: 'work-planning',
			icon: IconWorkPlan,
			value: 'work-planning',
		},
		{
			id: 'activities',
			labelKey: 'spendPlan.activities',
			path: 'activities',
			icon: IconActivity,
			value: 'activities',
		},
	],
};

/**
 * Financial Planning Group
 * Spend planning and payment conditions
 */
export const NAV_GROUP_FINANCIAL_PLANNING: NavMenuGroup = {
	id: 'financial-planning',
	labelKey: 'spendPlan.nav.group.financialPlanning',
	showDivider: true,
	items: [
		{
			id: 'spend-planning',
			labelKey: 'spendPlan.spendPlanning',
			path: 'spend-planning',
			icon: IconSpendPlan,
			value: 'spend-planning',
		},
		{
			id: 'funding-earmarks',
			labelKey: 'spendPlan.fundingEarmarks',
			path: 'funding-earmarks',
			icon: IconEarmark,
			value: 'funding-earmarks',
		},
	],
};

/**
 * Procurement Group
 * Contracts and procurement plan management
 */
export const NAV_GROUP_PROCUREMENT: NavMenuGroup = {
	id: 'procurement',
	labelKey: 'spendPlan.nav.group.procurement',
	showDivider: true,
	items: [
		{
			id: 'contracts',
			labelKey: 'contracts',
			path: 'contracts',
			icon: IconContract,
			value: 'contracts',
		},
		{
			id: 'suppliers',
			labelKey: 'suppliers',
			path: 'suppliers',
			icon: IconSupplier,
			value: 'suppliers',
		},
		{
			id: 'beneficiaries',
			labelKey: 'beneficiaries',
			path: 'beneficiaries',
			icon: IconBeneficiary,
			value: 'beneficiaries',
		},
		// {
		// 	id: 'procurement-plans',
		// 	labelKey: 'spendPlan.procurementPlans',
		// 	path: 'procurement-plans',
		// 	icon: IconProcurement,
		// 	value: 'procurement-plans',
		// },
	],
};

/**
 * Payments Group
 * Invoices and payment processing
 */
export const NAV_GROUP_PAYMENTS: NavMenuGroup = {
	id: 'payments',
	labelKey: 'spendPlan.nav.group.payments',
	showDivider: true,
	items: [
		{
			id: 'invoices',
			labelKey: 'spendPlan.invoices',
			path: 'invoices',
			icon: IconInvoice,
			value: 'invoices',
		},
		{
			id: 'payments',
			labelKey: 'spendPlan.payments',
			path: 'vouchers',
			icon: IconPayments,
			value: 'vouchers',
		},
	],
};

/**
 * Condition Verification Group
 * Payment conditions and verification group management
 */
export const NAV_GROUP_CONDITION_VERIFICATION: NavMenuGroup = {
	id: 'condition-verification',
	labelKey: 'spendPlan.nav.group.conditionVerification',
	showDivider: true,
	items: [
		{
			id: 'payment-conditions',
			labelKey: 'spendPlan.paymentConditions',
			path: 'payment-conditions',
			icon: IconCondition,
			value: 'payment-conditions',
		},
		{
			id: 'verification-groups',
			labelKey: 'spendPlan.verificationGroups',
			path: 'verification-groups',
			icon: IconVerificationGroup,
			value: 'verification-groups',
		},
	],
};

/**
 * Documents Group
 * Document management and viewing
 */
export const NAV_GROUP_DOCUMENTS: NavMenuGroup = {
	id: 'documents',
	labelKey: 'spendPlan.nav.group.documents',
	showDivider: true,
	items: [
		{
			id: 'all-documents',
			labelKey: 'spendPlan.documents.allDocuments',
			path: 'documents',
			icon: IconDocuments,
			value: 'documents',
		},
		{
			id: 'custom-templates',
			labelKey: 'spendPlan.customTemplates',
			path: 'custom-templates',
			icon: IconCustomTemplates,
			value: 'custom-templates',
			badge: 'new' as const,
		},
	],
};

/**
 * AI Chat (Top Level)
 * AI-powered agentic RAG chat interface
 */
export const NAV_ITEM_AI_CHAT: NavMenuItem = {
	id: 'ai-chat',
	labelKey: 'spendPlan.aiChat',
	path: 'chat',
	icon: IconAIChat,
	value: 'chat',
	badge: 'new' as const,
};

/**
 * Reports (Top Level)
 * Reporting and analytics - not part of a group
 */
export const NAV_ITEM_REPORTS: NavMenuItem = {
	id: 'reports',
	labelKey: 'spendPlan.reports.title',
	path: 'reports',
	icon: IconReport,
	value: 'reports',
};

/**
 * Project Group (v1-backed routes surfaced in the v2 sidebar)
 *
 * Brings the legacy Project tabs (Overview, Financing, Entities, Accounts,
 * Fund Flow, Settings) into the v2 sidebar so the v2 experience is sidebar-only.
 * The v1 Overview is the project Overview entry; the v2 Planning Summary now
 * lives under the Activity Planning group (see `NAV_ITEM_FINANCE_OVERVIEW`).
 * v1 Reports is reused as the project Reports tab — the v2 sidebar's existing
 * `NAV_ITEM_REPORTS` is removed from the bottom items in favor of this entry.
 */
export const NAV_GROUP_PROJECT: NavMenuGroup = {
	id: 'project',
	labelKey: 'spendPlan.nav.group.project',
	showDivider: true,
	items: [
		{
			id: 'project-overview',
			labelKey: 'overview',
			path: 'overview',
			icon: IconDashboard,
			value: 'overview',
			absolute: true,
		},
		{
			id: 'project-financing',
			labelKey: 'financing',
			path: 'financing',
			icon: IconFinancing,
			value: 'financing',
			absolute: true,
		},
		{
			// v1 Transactions screen surfaced read-only in v2 — see
			// BLDT/docs/v2-payment-architecture.md. Once bldt-api-fn mirrors
			// completed v2 vouchers into the v1 `transaction` table, those rows
			// (and the v1 reimbursement row action) appear here automatically.
			id: 'project-transactions',
			labelKey: 'transactions',
			path: 'transactions',
			icon: IconPayments,
			value: 'transactions',
			absolute: true,
		},
		{
			id: 'project-entities',
			labelKey: 'entities',
			path: 'entities',
			icon: IconEntities,
			value: 'project-entities',
			absolute: true,
		},
		{
			id: 'project-accounts',
			labelKey: 'accounts',
			path: 'accounts',
			icon: IconAccount,
			value: 'accounts',
			absolute: true,
		},
		{
			id: 'project-reports',
			labelKey: 'reports',
			path: 'reports',
			icon: IconReport,
			value: 'reports',
			absolute: true,
		},
		{
			id: 'project-settings',
			labelKey: 'settings',
			path: 'settings',
			icon: IconSettings,
			value: 'settings',
			absolute: true,
		},
	],
};

// Complete Navigation Configuration

/**
 * Top-level navigation items (not part of any group)
 */
export const NAV_TOP_LEVEL_ITEMS: NavMenuItem[] = [];

/**
 * Bottom-level navigation items (not part of any group, appear after groups)
 */
export const NAV_BOTTOM_LEVEL_ITEMS: NavMenuItem[] = [NAV_ITEM_AI_CHAT];

/**
 * All navigation groups in display order
 */
export const NAV_GROUPS: NavMenuGroup[] = [
	NAV_GROUP_PROJECT,
	NAV_GROUP_ACTIVITY_PLANNING,
	NAV_GROUP_FINANCIAL_PLANNING,
	NAV_GROUP_PROCUREMENT,
	NAV_GROUP_PAYMENTS,
	NAV_GROUP_CONDITION_VERIFICATION,
	NAV_GROUP_DOCUMENTS,
];

/**
 * Flat list of all navigation items (useful for lookups and iteration)
 */
export const NAV_ITEMS: NavMenuItem[] = [
	...NAV_TOP_LEVEL_ITEMS,
	...NAV_GROUPS.flatMap((group) => group.items),
	...NAV_BOTTOM_LEVEL_ITEMS,
];

/**
 * Complete navigation configuration
 */
export const NAV_CONFIG: NavMenuConfig = {
	groups: NAV_GROUPS,
	allItems: NAV_ITEMS,
};

// Route Mapping Utilities

/**
 * Maps singular detail route segments to their corresponding navigation values
 * Used for determining active tab when viewing detail pages
 */
export const ROUTE_TO_NAV_VALUE_MAP: Record<string, string> = {
	// Activities
	activity: 'activities',
	activities: 'activities',
	// Work Planning
	workPlan: 'work-planning',
	workPlans: 'work-planning',
	'work-plan': 'work-planning',
	'work-plans': 'work-planning',
	// Components & Categories
	'components-categories': 'components-categories',
	// Contracts
	contract: 'contracts',
	contracts: 'contracts',
	// Suppliers
	supplier: 'suppliers',
	suppliers: 'suppliers',
	// Beneficiaries
	beneficiary: 'beneficiaries',
	beneficiaries: 'beneficiaries',
	// Invoices
	invoice: 'invoices',
	invoices: 'invoices',
	// Procurement
	'procurement-plan': 'procurement-plans',
	'procurement-plans': 'procurement-plans',
	// Client Custom Templates
	'custom-templates': 'custom-templates',
	// Funding Earmarks
	'funding-earmarks': 'funding-earmarks',
	// Payment Conditions
	condition: 'payment-conditions',
	conditions: 'payment-conditions',
	'payment-condition': 'payment-conditions',
	'payment-conditions': 'payment-conditions',
	// Payments
	payment: 'vouchers',
	payments: 'vouchers',
	voucher: 'vouchers',
	vouchers: 'vouchers',
	// Verification Groups
	'verification-group': 'verification-groups',
	'verification-groups': 'verification-groups',
	// Documents
	documents: 'documents',
	'all-documents': 'documents',
	// Reports
	reports: 'reports',
	'activity-monitoring': 'reports',
	'payment-report': 'reports',
	'operations-dashboard': 'reports',
	// AI Chat
	chat: 'chat',
	// v1 Project routes (surfaced as Project group in v2 sidebar)
	financing: 'financing',
	transactions: 'transactions',
	entities: 'project-entities',
	accounts: 'accounts',
	flow: 'fundFlow',
	settings: 'settings',
};

/**
 * Get navigation item by its value/id
 */
export const getNavItemByValue = (value: string): NavMenuItem | undefined => {
	return NAV_ITEMS.find((item) => item.value === value || item.id === value);
};

/**
 * Get navigation group that contains a specific item
 */
export const getNavGroupByItemValue = (value: string): NavMenuGroup | undefined => {
	return NAV_GROUPS.find((group) => group.items.some((item) => item.value === value));
};

/**
 * Build full route path for a navigation item
 */
export const buildNavItemPath = (item: NavMenuItem, projectId: string | number): string => {
	if (item.absolute) {
		return item.path ? `/project/${projectId}/${item.path}` : `/project/${projectId}`;
	}
	const basePath = `/project/${projectId}/spend-plans`;
	return item.path ? `${basePath}/${item.path}` : basePath;
};

/**
 * Determine current navigation value from URL pathname
 */
export const getCurrentNavValue = (pathname: string): string => {
	const pathSegments = pathname.split('/');
	const spendPlansIndex = pathSegments.indexOf('spend-plans');

	// Inside the v2 spend-plans subtree → use the segment after `spend-plans`
	if (spendPlansIndex !== -1) {
		const segmentAfterSpendPlans = pathSegments[spendPlansIndex + 1];
		if (segmentAfterSpendPlans && ROUTE_TO_NAV_VALUE_MAP[segmentAfterSpendPlans]) {
			return ROUTE_TO_NAV_VALUE_MAP[segmentAfterSpendPlans];
		}
		return !segmentAfterSpendPlans || segmentAfterSpendPlans === 'dashboard'
			? 'dashboard'
			: segmentAfterSpendPlans;
	}

	// Outside the spend-plans subtree (e.g. /project/:id/financing) → use the
	// segment immediately after the project id.
	const projectIndex = pathSegments.indexOf('project');
	if (projectIndex !== -1) {
		const segmentAfterProjectId = pathSegments[projectIndex + 2];
		if (segmentAfterProjectId && ROUTE_TO_NAV_VALUE_MAP[segmentAfterProjectId]) {
			return ROUTE_TO_NAV_VALUE_MAP[segmentAfterProjectId];
		}
		// Project root (/project/:id) renders the v1 Overview tab → highlight Overview
		return segmentAfterProjectId || 'overview';
	}

	return 'overview';
};

// Translation Keys (for reference when adding to translation files)

/**
 * Translation keys used for navigation.
 * These keys should exist in public/locales/en/translation.json:
 *
 * Groups:
 * "spendPlan.nav.group.planning": "Planning",
 * "spendPlan.nav.group.procurement": "Procurement",
 * "spendPlan.nav.group.payments": "Payments",
 *
 * Items:
 * "spendPlan.invoices": "Invoices"
 */
export const NAV_TRANSLATION_KEYS = {
	groups: {
		planning: 'spendPlan.nav.group.planning',
		procurement: 'spendPlan.nav.group.procurement',
		payments: 'spendPlan.nav.group.payments',
		documents: 'spendPlan.nav.group.documents',
	},
	items: {
		financeOverview: 'spendPlan.financeOverview',
		activities: 'spendPlan.activities',
		workPlanning: 'spendPlan.workPlanning',
		spendPlanning: 'spendPlan.spendPlanning',
		paymentConditions: 'spendPlan.paymentConditions',
		contracts: 'contracts',
		suppliers: 'suppliers',
		beneficiaries: 'beneficiaries',
		procurementPlans: 'spendPlan.procurementPlans',
		clientCustomTemplates: 'spendPlan.customTemplates',
		invoices: 'spendPlan.invoices',
		payments: 'spendPlan.payments',
		allDocuments: 'spendPlan.documents.allDocuments',
		aiChat: 'spendPlan.aiChat',
		reports: 'spendPlan.reports.title',
	},
} as const;
